/*
* Analytics.ts
* @author Abhilash Panwar (abpanwar)
* @copyright Microsoft 2020
* File containing Analytics object.
*/
import { AppInsightsCore, CoreUtils, Utils, isDocumentObjectAvailable, getDocument, NotificationManager } from '@microsoft/1ds-core-js';
import { PostChannel, RT_PROFILE } from '@microsoft/1ds-post-js';
import { _Extensions, _SignalExtKeys, _PageViewType, _SharedKeys, _ViewExtKeys, _ReferrerExtKeys, _EIPreviousKey, _ViewEventName, _ViewSchemaVersion, _ActionEventName, _ActionSchemaVersion, _ActionExtKeys, _ClickExtKeys, _UserMappingEventName, _UserMappingEventVersion, _UserMappingStorageKey, _DayToMilliseconds, _StatsEventName, _StatsSchemaVersion } from './properties/Constants';
import Properties from './properties/Properties';
import Autocollector from './analytics/Autocollector';
import StorageManager from './properties/StorageManager';
import SessionManager from './properties/SessionManager';
import StatsManager from './properties/StatsManager';
import { _getPathNameFromUri, _hashCode } from './analytics/AnalyticsHelper';
var d = decodeURI; // for code minimization purpose
/**
 * Engagement Insights class that is used to send events.
 */
var Analytics = /** @class */ (function () {
    function Analytics() {
        this._properties = new Properties();
        this._isInitialized = false;
        this._autoCollector = new Autocollector();
        this._invalidSignalPrefix = _Extensions._SignalExt + '.';
        this._invalidSignalLongPrefix = _Extensions._SignalLongExt + '.';
    }
    /**
     * Initializes the Analytics class according to the passed configuration. It will send autocollected events if the passed
     * configuration specifies it.
     * @param configuration the configuration used to initialize the Analytics class
     */
    Analytics.prototype.initialize = function (configuration) {
        var _this = this;
        if (!this._isInitialized) {
            this._isInitialized = true;
            this._configuration = configuration;
            this._applicationInsightsCore = new AppInsightsCore();
            this._postChannel = new PostChannel();
            var profile = {};
            profile[RT_PROFILE] = [2, 0];
            this._postChannel._loadTransmitProfiles(profile);
            this._storageDisabled = Utils.isValueAssigned(configuration.userConsent) ? !configuration.userConsent : false;
            this._storageDisabled = StorageManager._getLocalStorage() ? this._storageDisabled : true;
            this._storageManager = new StorageManager(this._storageDisabled);
            //Only enable session if the user has given consent.
            if (!this._storageDisabled) {
                this._sessionManager = new SessionManager(this._storageManager);
                this._properties.setup(this._sessionManager);
            }
            //Initialize the core
            var coreConfig = {
                instrumentationKey: configuration.ingestionKey,
                endpointUrl: configuration.endpointUrl,
                disableCookiesUsage: this._storageDisabled,
                extensions: [this._properties, this._postChannel],
                extensionConfig: []
            };
            var postConfig = {
                ignoreMc1Ms0CookieProcessing: true
            };
            coreConfig.extensionConfig[this._postChannel.identifier] = postConfig;
            this._statsManager = new StatsManager(function (stats, ingestionKey) {
                var hasStats = false;
                var event = {
                    name: _StatsEventName,
                    timestamp: new Date(),
                    version: _StatsSchemaVersion,
                    ingestionKey: ingestionKey
                };
                var props = {};
                for (var statKey in stats) {
                    if (stats.hasOwnProperty(statKey)) {
                        props[statKey] = stats[statKey].toString();
                        hasStats = true;
                    }
                }
                event.properties = props;
                if (hasStats) {
                    event.properties = event.properties || {};
                    _this._sanitizeProperties(event.properties);
                    _this._trackEvent(event);
                }
            });
            this._notificationManager = new NotificationManager();
            this._notificationManager.addNotificationListener({
                eventsSent: this._statsManager._addStatForEventSent.bind(this._statsManager),
                eventsDiscarded: this._statsManager._addStatForEventDiscarded.bind(this._statsManager)
            });
            this._applicationInsightsCore.initialize(coreConfig, [], null, this._notificationManager);
            //Pass autocapture config to Autocollector
            this._autoCollector._autocollect(this, configuration.autoCapture);
            // send stored usermapping if setUser is called before initialize
            if (this._setUserBeforeInitialize && this._configuration.userMapping) {
                this._updateUserMapping(this._user);
            }
        }
    };
    /**
     * Tracks a custom event to be sent to Engagement Insights.
     * @param event event to be sent
     */
    Analytics.prototype.trackEvent = function (event) {
        if (event.name !== _StatsEventName) {
            event.properties = event.properties || {};
            this._sanitizeProperties(event.properties);
            this._trackEvent(event);
        }
    };
    /**
     * Tracks a view event to be sent to Engagement Insights.
     * @param view view event to be sent
     */
    Analytics.prototype.trackView = function (view) {
        if (!this._storageDisabled) {
            if (!view || !Utils.isObject(view) || !Utils.isString(view.uri) || view.uri.length === 0) {
                throw 'View event should have uri';
            }
            this._lastViewTime = view.timestamp || new Date();
            var event_1 = {
                name: _ViewEventName,
                version: _ViewSchemaVersion,
                timestamp: this._lastViewTime
            };
            var props = view.properties || {};
            this._sanitizeProperties(props);
            // There should only be a previous view loaded if the session is ongoing.
            if (this._sessionManager._isSessionActive(this._lastViewTime.getTime()), true) {
                this._previousView = this._storageManager._getProperty(_EIPreviousKey);
            }
            this._addAutocollectableFieldsToView(view);
            this._addViewInfo(view, props);
            this._addScreenInfo(props);
            this._lastView = view;
            event_1.properties = props;
            this._trackEvent(event_1);
            this._storageManager._setProperty(_EIPreviousKey, view.uri);
        }
    };
    /**
     * Tracks an action event to be sent to Engagement Insights.
     * @param action action event to be sent
     * @param properties custom properties that are part of this event as defined by the emitter.
     */
    Analytics.prototype.trackAction = function (action, properties) {
        if (!this._storageDisabled) {
            if (!this._lastView) {
                throw 'Views should be instrumented before actions are sent';
            }
            if (!Utils.isValueAssigned(action.type)) {
                throw 'Action event should have type';
            }
            var actionTime = action.timestamp || new Date();
            // If this is the action event that starts a session then send a view event from the last view before the action is sent. 
            if (!this._sessionManager._isSessionActive(actionTime.getTime())) {
                this._lastView.timestamp = actionTime;
                this.trackView(this._lastView);
            }
            var event_2 = {
                name: _ActionEventName,
                version: _ActionSchemaVersion,
                timestamp: actionTime
            };
            var props = action.properties || {};
            this._sanitizeProperties(props);
            this._addViewInfo(this._lastView, props);
            this._addScreenInfo(props);
            //Add action properties
            props[_Extensions._SignalExt][_SignalExtKeys._Action] = {};
            props[_Extensions._SignalExt][_SignalExtKeys._Action][_SharedKeys._NameKey] = action.name;
            props[_Extensions._SignalExt][_SignalExtKeys._Action][_SharedKeys._Type] = action.type;
            props[_Extensions._SignalExt][_SignalExtKeys._Action][_ActionExtKeys._ElementId] = action.elementId;
            props[_Extensions._SignalExt][_SignalExtKeys._Action][_SharedKeys._Class] = action["class"];
            props[_Extensions._SignalExt][_SignalExtKeys._Action][_ActionExtKeys._Target] = d(action.viewTarget);
            //Elapsed time
            props[_Extensions._SignalExt][_SignalExtKeys._Action][_ActionExtKeys._ElapsedTime] = actionTime.getTime() - this._lastViewTime.getTime();
            //Outbound. Only check if there is a view target and we have an origin for the webpage.
            if (action.viewTarget && this._properties.origin) {
                props[_Extensions._SignalExt][_SignalExtKeys._Action][_ActionExtKeys._IsOutbound] = !action.viewTarget.startsWith(this._properties.origin);
            }
            props[_Extensions._SignalExt][_SignalExtKeys._Click] = {};
            props[_Extensions._SignalExt][_SignalExtKeys._Click][_ClickExtKeys._Coordinates] = action.clickCoordinates;
            event_2.properties = props;
            this._trackEvent(event_2);
        }
    };
    /**
     * Teardown the SDK, and flushes all events currenlty in the queue.
     * After this method the SDK can be re-initialized with a new configuration.
     */
    Analytics.prototype.teardown = function () {
        if (this._isInitialized) {
            this._postChannel.teardown();
            this._isInitialized = false;
            //Reset properties      
            this._properties = new Properties();
            this._autoCollector._removeAllListeners();
            this._statsManager._teardown();
        }
    };
    /**
     * Set custom property to be sent with every event.
     * @param name name of property
     * @param value value for the property
     */
    Analytics.prototype.setProperty = function (name, value) {
        if (this._isValidKey(name)) {
            this._properties.setProperty(name, value);
        }
    };
    /**
     * Set user details to be sent with every event. Update UserMapping depending on whether the SDK is initialized or not
     * @param user user details
     */
    Analytics.prototype.setUser = function (user) {
        this._properties.setUser(user);
        if (this._isInitialized && this._configuration.userMapping) {
            this._updateUserMapping(user);
        }
        else {
            this._user = user;
            this._setUserBeforeInitialize = true;
        }
    };
    Analytics.prototype._trackEvent = function (event) {
        if (this._isInitialized) {
            var item = {
                name: event.name,
                iKey: event.ingestionKey || this._configuration.ingestionKey
            };
            var props = event.properties;
            var time = event.timestamp ? event.timestamp : new Date();
            item.time = CoreUtils.toISOString(time);
            props[_Extensions._SignalExt][_SignalExtKeys._ExtKey] = {};
            props[_Extensions._SignalExt][_SignalExtKeys._ExtKey][_SharedKeys._Id] = Utils.createGuid();
            if (!CoreUtils.isNullOrUndefined(event.version)) {
                props[_Extensions._SignalExt][_SignalExtKeys._ExtKey][_SharedKeys._Version] = event.version;
            }
            item.data = props;
            this._applicationInsightsCore.track(item);
        }
    };
    Analytics.prototype._sanitizeProperties = function (props) {
        var _this = this;
        // Don't allow customer to send any fields in the event extension. It is reserved for EI.
        Utils.arrForEach(Utils.objKeys(props), function (key) {
            if (!_this._isValidKey(key)) {
                delete props[key];
            }
        });
        props[_Extensions._SignalExt] = {};
    };
    Analytics.prototype._isValidKey = function (key) {
        key = key.toLowerCase();
        return !(key.length < 3 || key.startsWith(this._invalidSignalPrefix) || key.startsWith(this._invalidSignalLongPrefix) || key === _Extensions._SignalLongExt);
    };
    Analytics.prototype._addScreenInfo = function (props) {
        // Get screen resolution
        var screen = this._autoCollector._getScreenResolution();
        if (screen) {
            props[_Extensions._SignalExt][_SignalExtKeys._View][_ViewExtKeys._Height] = screen.h;
            props[_Extensions._SignalExt][_SignalExtKeys._View][_ViewExtKeys._Width] = screen.w;
            props[_Extensions._SignalExt][_SignalExtKeys._View][_ViewExtKeys._VpHeight] = screen.vH;
            props[_Extensions._SignalExt][_SignalExtKeys._View][_ViewExtKeys._VpWidth] = screen.vW;
            props[_Extensions._SignalExt][_SignalExtKeys._View][_ViewExtKeys._ColorDepth] = screen.cd;
            props[_Extensions._SignalExt][_SignalExtKeys._View][_ViewExtKeys._SResolution] = screen.sR;
        }
    };
    Analytics.prototype._addViewInfo = function (view, props) {
        props[_Extensions._SignalExt][_SignalExtKeys._View] = {};
        props[_Extensions._SignalExt][_SignalExtKeys._View][_SharedKeys._Title] = view.title;
        props[_Extensions._SignalExt][_SignalExtKeys._View][_SharedKeys._Type] = _PageViewType;
        props[_Extensions._SignalExt][_SignalExtKeys._View][_SharedKeys._Uri] = d(view.uri);
        props[_Extensions._SignalExt][_SignalExtKeys._View][_SharedKeys._NameKey] = d(view.name);
        props[_Extensions._SignalExt][_SignalExtKeys._View][_SharedKeys._Class] = view.viewClass;
        props[_Extensions._SignalExt][_SignalExtKeys._View][_ViewExtKeys._PreviousViews] = this._previousView;
        if (view.referrer && Utils.isObject(view.referrer)) {
            props[_Extensions._SignalExt][_SignalExtKeys._Referrer] = {};
            props[_Extensions._SignalExt][_SignalExtKeys._Referrer][_ReferrerExtKeys._PageTitle] = view.referrer.pageTitle;
            props[_Extensions._SignalExt][_SignalExtKeys._Referrer][_SharedKeys._Uri] = d(view.referrer.uri);
        }
    };
    Analytics.prototype._addAutocollectableFieldsToView = function (view) {
        view.name = view.name || this._autoCollector._getPageNameFromPath(_getPathNameFromUri(view.uri));
        if (isDocumentObjectAvailable) {
            view.referrer = view.referrer || this._autoCollector._getReferrer(getDocument());
        }
    };
    Analytics.prototype._updateUserMapping = function (user) {
        var time = CoreUtils.dateNow();
        var localId = user.localId || this._properties._msei;
        if (localId && user.authId) {
            var usermappingKey = _hashCode(localId + ',' + user.authId).toString();
            var storedMapping = JSON.parse(this._storageManager._getProperty(_UserMappingStorageKey)) || {};
            if (!CoreUtils.hasOwnProperty(storedMapping, usermappingKey) || time - parseInt(storedMapping[usermappingKey]) > 7 * _DayToMilliseconds) {
                storedMapping[usermappingKey] = time.toString();
                this._storageManager._setProperty(_UserMappingStorageKey, JSON.stringify(storedMapping));
                var event_3 = {
                    name: _UserMappingEventName,
                    version: _UserMappingEventVersion,
                    properties: {}
                };
                this._sanitizeProperties(event_3.properties);
                this._trackEvent(event_3);
            }
            if (Utils.objKeys(storedMapping).length > 5) {
                this._deleteOldestUserMapping(storedMapping);
            }
        }
    };
    Analytics.prototype._deleteOldestUserMapping = function (storedMapping) {
        var oldestMappingKey = '';
        var oldestTimestamp = Number.MAX_VALUE;
        Utils.arrForEach(Utils.objKeys(storedMapping), function (key) {
            if (parseInt(storedMapping[key]) < oldestTimestamp) {
                oldestMappingKey = key;
                oldestTimestamp = parseInt(storedMapping[key]);
            }
        });
        delete storedMapping[oldestMappingKey];
        this._storageManager._setProperty(_UserMappingStorageKey, JSON.stringify(storedMapping));
    };
    return Analytics;
}());
export default Analytics;
//# sourceMappingURL=Analytics.js.map